import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../services/api';
import './Wishes.css';

const WishFormFixed = ({ editingWish, onWishSubmitted, onCancelEdit }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useUserAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (editingWish) {
      setMessage(editingWish.message || '');
    } else {
      setMessage('');
    }
  }, [editingWish]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError(t('wishes.errorEmptyMessage'));
      return;
    }

    if (!user) {
      setError(t('wishes.errorNotLoggedIn'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let response;
      
      if (editingWish) {
        // Update existing wish
        response = await api.put(
          `/wishes/${editingWish.id}`, 
          { message }
        );
        setSuccess(t('wishes.successUpdate'));
      } else {
        // Create new wish
        response = await api.post(
          '/wishes', 
          { message }
        );
        setSuccess(t('wishes.successCreate'));
      }

      // Clear form and notify parent
      setMessage('');
      if (onWishSubmitted) {
        onWishSubmitted(response.data.wish);
      }
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
    } catch (err) {
      console.error('Wish submission error:', err);
      console.log('Error details:', JSON.stringify(err.response?.data || {}));
      
      if (err.response?.status === 403) {
        setError(t('wishes.errorWishesDisabled'));
      } else if (err.response?.status === 401) {
        setError('Authentication error. Please try logging out and back in.');
      } else {
        setError(`${t('wishes.errorSubmissionFailed')} (${err.message})`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setMessage('');
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  if (!user) {
    return (
      <Card className="wish-form-card">
        <Card.Body>
          <Card.Title>{t('wishes.leaveWish')}</Card.Title>
          <Card.Text>
            {t('wishes.loginToLeaveWish')}
          </Card.Text>
          <Button 
            variant="primary" 
            href="/login"
          >
            {t('login')}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="wish-form-card">
      <Card.Body>
        <Card.Title>
          {editingWish ? t('wishes.editYourWish') : t('wishes.leaveWish')}
        </Card.Title>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('wishes.messagePlaceholder')}
              rows={5}
              maxLength={500}
              required
            />
            <Form.Text className="text-muted d-block text-end">
              {message.length}/500
            </Form.Text>
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            {editingWish ? (
              <>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <FaTimes className="me-1" /> {t('cancel')}
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={loading || !message.trim()}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t('saving')}
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="me-2" />
                      {t('update')}
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button 
                variant="primary" 
                type="submit"
                className="w-100"
                disabled={loading || !message.trim()}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('sending')}
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="me-2" />
                    {t('wishes.sendButton')}
                  </>
                )}
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default WishFormFixed;